import { router } from '@inertiajs/react'
import { Download } from 'lucide-react'
import DateRangeFilter, { DateRange } from './DateRangeFilter'

const REPORT_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'last_7_days', label: 'Last 7 Days' },
  { key: 'last_30_days', label: 'Last 30 Days' },
  { key: 'last_90_days', label: 'Last 90 Days' },
  { key: 'quarter_to_date', label: 'Quarter to Date' },
  { key: 'year_to_date', label: 'Year to Date' },
  { key: 'all_time', label: 'All Time' },
]

interface Props {
  path: string
  dateRange: DateRange
  filters?: Record<string, string | number | undefined | null>
  sortState?: { key?: string | null; dir?: string | null }
  hideDateRange?: boolean
  children?: React.ReactNode
}

export default function ReportControlBar({ path, dateRange, filters, sortState, hideDateRange, children }: Props) {
  const mergedFilters = { ...(filters || {}) }
  if (sortState?.key) mergedFilters.sort = sortState.key
  if (sortState?.dir) mergedFilters.dir = sortState.dir

  function buildExportUrl() {
    const params = new URLSearchParams()
    if (!hideDateRange && dateRange?.preset) params.set('preset', dateRange.preset)
    if (!hideDateRange && dateRange?.preset === 'custom') {
      if (dateRange.start_date) params.set('start_date', dateRange.start_date)
      if (dateRange.end_date) params.set('end_date', dateRange.end_date)
    }
    Object.entries(mergedFilters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.set(k, String(v))
    })
    const qs = params.toString()
    return `${path}.csv${qs ? `?${qs}` : ''}`
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white border border-chestnut-gray rounded-xl p-4">
      <div className="flex flex-wrap items-center gap-3">
        {children}
      </div>
      <div className="flex items-center gap-2">
        {!hideDateRange && (
          <DateRangeFilter
            dateRange={dateRange}
            path={path}
            presets={REPORT_PRESETS}
            extraParams={mergedFilters}
          />
        )}
        <a
          href={buildExportUrl()}
          className="flex items-center gap-2 px-4 py-2.5 bg-chestnut-forest text-white rounded-lg text-sm font-medium hover:bg-chestnut-forest/90 transition-colors shadow-sm"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </a>
      </div>
    </div>
  )
}

interface SelectFilterProps {
  label: string
  value: string | number | undefined | null
  options: { value: string | number; label: string }[]
  onChange: (value: string) => void
  allLabel?: string
}

export function SelectFilter({ label, value, options, onChange, allLabel = 'All' }: SelectFilterProps) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-chestnut-charcoal/70">{label}</span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-chestnut-forest"
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}

export function applyFilter(path: string, current: Record<string, string | number | undefined | null>, key: string, value: string) {
  const merged = { ...current, [key]: value }
  const params: Record<string, string | number> = {}
  Object.entries(merged).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') params[k] = v
  })
  router.get(path, params, { preserveState: false })
}

export function makeSortHandler(
  path: string,
  dateRange: DateRange,
  filters: Record<string, string | number | undefined | null>
) {
  return (key: string, direction: 'asc' | 'desc') => {
    const params: Record<string, string | number> = {}
    if (dateRange?.preset) params.preset = dateRange.preset
    if (dateRange?.preset === 'custom') {
      if (dateRange.start_date) params.start_date = dateRange.start_date
      if (dateRange.end_date) params.end_date = dateRange.end_date
    }
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params[k] = v as string | number
    })
    params.sort = key
    params.dir = direction
    router.get(path, params, { preserveState: false })
  }
}
